import React from 'react';
import { Rocket, Trophy, Star } from 'lucide-react';
import { fmtInt, fmtGifts, fmtUSD, fmtHours } from './starFormat';
import { StarProgressBar, ObsidianCard } from './StarBits';

const Goal = ({ label, current, target, pct, achieved, unit = 'cristais', testId }) => (
  <div className="space-y-1.5" data-testid={testId}>
    <div className="flex flex-wrap items-center justify-between gap-1">
      <span className="text-[11px] font-semibold text-zinc-300">{label}</span>
      <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${achieved
        ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300'
        : 'border-white/10 bg-white/[0.04] text-zinc-400'}`}>
        {achieved ? 'cumprido' : `${pct}%`}
      </span>
    </div>
    <StarProgressBar pct={pct} achieved={achieved} thin />
    <p className="font-mono text-[11px] text-zinc-500">
      {fmtInt(current)} de {fmtInt(target)} {unit}
    </p>
  </div>
);

export const HostPromotionCard = ({ promo }) => {
  if (!promo) return null;

  if (promo.case === 'max_level') {
    return (
      <ObsidianCard testId="host-promotion-section" aura="gold">
        <div className="flex items-start gap-3">
          <Trophy className="mt-0.5 h-6 w-6 shrink-0 text-amber-400" />
          <div>
            <p className="font-display text-sm font-black text-white">Você está no nível máximo (SS)</p>
            <p className="mt-1 text-xs leading-relaxed text-zinc-400">
              Não existe promoção acima — mantenha a meta do ciclo para continuar no topo da agência.
            </p>
          </div>
        </div>
      </ObsidianCard>
    );
  }

  const title = promo.case === 'become'
    ? 'Como virar Star Host'
    : promo.case === 'reactivate'
      ? 'Reativar seu nível (Pausado → A)'
      : `Subir para o nível ${promo.target_level}`;

  return (
    <ObsidianCard testId="host-promotion-section" aura="violet" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-display text-sm font-bold text-white">
          {promo.case === 'become'
            ? <Star className="h-4 w-4 text-amber-400" />
            : <Rocket className="h-4 w-4 text-violet-400" />}
          {title}
        </p>
        <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 font-mono text-[10px] text-zinc-400">
          {promo.window.start} → {promo.window.end}
        </span>
      </div>

      {promo.case === 'become' ? (
        <Goal testId="host-promo-target-income" label="Presentes da sorte recebidos"
          current={promo.luck_gifts.current} target={promo.luck_gifts.target}
          pct={promo.luck_gifts.pct} achieved={promo.luck_gifts.achieved}
          unit="em presentes da sorte" />
      ) : (
        <>
          <Goal testId="host-promo-target-income" label="Comissão em cristais na janela"
            current={promo.income.current} target={promo.income.target}
            pct={promo.income.pct} achieved={promo.income.achieved} />
          {!promo.income.achieved && (
            <p className="rounded-2xl border border-violet-400/20 bg-violet-400/[0.07] p-2.5 text-[11px] leading-relaxed text-zinc-300"
              data-testid="host-promo-equivalence">
              Faltam <span className="font-mono font-bold text-white">{fmtInt(promo.income.remaining)}</span>{' '}
              cristais = <span className="font-mono font-bold text-amber-300">
                {fmtGifts(promo.income.equivalences.exclusive_gifts)}</span> presentes exclusivos ou{' '}
              <span className="font-mono font-bold text-emerald-300">
                {fmtGifts(promo.income.equivalences.luck_gifts)}</span> presentes da sorte{' '}
              <span className="font-mono text-zinc-400">({fmtUSD(promo.income.equivalences.usd)})</span>
            </p>
          )}
        </>
      )}

      {promo.live_days && (
        <>
          <Goal testId="host-promo-target-days"
            label={`Dias de live de vídeo (${fmtHours(promo.live_days.min_video_minutes)} por dia)`}
            current={promo.live_days.current} target={promo.live_days.target}
            pct={promo.live_days.pct} achieved={promo.live_days.achieved} unit="dia(s)" />
          {promo.live_days.valid_days_source === 'kako_star_host' && (
            <p className="rounded-2xl border border-white/10 bg-white/[0.03] p-2.5 text-[10px] leading-relaxed text-zinc-400"
              data-testid="host-promo-days-source">
              No ciclo atual os dias vêm dos <span className="text-zinc-200">dias válidos do Star Host da KAKO</span>
              {' '}({fmtInt(promo.live_days.current_cycle_valid_days)} dia(s)) — a isenção de 1h já está aplicada aí.
              {promo.live_days.exemption_applied
                ? ` Sua regra está em ${fmtHours(promo.live_days.min_video_minutes)} por dia (isenção de 1h sobre ${fmtHours(promo.live_days.base_min_video_minutes)}).`
                : ` Sua regra está em ${fmtHours(promo.live_days.base_min_video_minutes)} por dia; com isenção de 1h cairia para ${fmtHours(promo.live_days.base_min_video_minutes - 60)}.`}
            </p>
          )}
        </>
      )}

      <p className="text-[10px] leading-relaxed text-zinc-500" data-testid="host-promo-violations-note">
        {promo.case !== 'become' && promo.max_violations
          ? `Exige também menos de ${promo.max_violations} violações no período — a KAKO não envia essa contagem, quem confere é a administração. `
          : ''}
        A avaliação acontece nos dias 1º e 16 de cada mês. Live de áudio não conta.
      </p>
    </ObsidianCard>
  );
};
